/**
 * Sparkline — courbe miniature posée dans une tuile KPI.
 *
 * Sans axes ni infobulle, volontairement : elle ne sert pas à lire une valeur
 * mais à dire « ce chiffre monte » ou « ce chiffre s'est effondré cette nuit ».
 * La valeur exacte est déjà écrite en gros juste au-dessus.
 */

import { buildPath, describeSeries } from './chartUtils';
import type { SeriesPoint } from './chartUtils';

export interface SparklineProps {
  label: string;
  points: SeriesPoint[];
  color?: string;
  height?: number;
}

const W = 160;

export function Sparkline(props: SparklineProps): React.ReactElement | null {
  const { label, points, color = 'var(--c-blue)', height = 34 } = props;
  // Une sparkline d'un point est un pixel : mieux vaut ne rien afficher que
  // suggérer une tendance qui n'existe pas.
  if (points.length < 2) return null;

  const values = points.map((p) => p.v);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const xOf = (i: number): number => (i / (points.length - 1)) * W;
  const yOf = (v: number): number => height - 3 - ((v - min) / span) * (height - 6);

  const d = buildPath(values, xOf, yOf);
  const last = values[values.length - 1];

  // Pas de classe `kpi-spark` ici : elle appartient à l'enveloppe posée par
  // `<Kpi>`. La porter aux deux niveaux appliquerait deux fois la même marge.
  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      role="img"
      aria-label={describeSeries(label, [{ key: label, label, color, points }])}
      preserveAspectRatio="none"
      style={{ width: '100%', height }}
    >
      <path
        d={`${d} L${W},${height} L0,${height} Z`}
        fill={color}
        opacity={0.12}
        stroke="none"
      />
      <path d={d} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {last === undefined ? null : <circle cx={W} cy={yOf(last)} r={2.4} fill={color} />}
    </svg>
  );
}
